import React, { useState, useEffect } from 'react';
import {
    ArrowLeft as ArrowBackIcon,
    BadgeDollarSign as AttachMoneyIcon,
    FileText as DescriptionIcon,
    NotebookText as NotesIcon,
    Smartphone as PhoneAndroidIcon,
    User as PersonIcon,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getQuoteById, updateQuoteWithType } from '../../api/action/quote.jsx';
import { Box, Container, Grid, sxToStyle, Typography } from '../../components/ui/LayoutPrimitives';
import { Snackbar } from '../../components/ui/Feedback';

const Button = ({ children, className = '', style, sx, type = 'button', variant, ...props }) => (
    <button
        type={type}
        className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${variant === 'contained' ? 'bg-[#195790] text-white' : 'bg-transparent'} ${className}`}
        style={{ ...sxToStyle(sx), ...(style || {}) }}
        {...props}
    >
        {children}
    </button>
);

const Card = ({ children, className = '', elevation, style, sx }) => (
    <div className={`rounded-lg bg-white ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
        {children}
    </div>
);

const CardContent = ({ children, className = '' }) => <div className={`p-4 ${className}`}>{children}</div>;

const CircularProgress = ({ size = 40, style, sx }) => (
    <span
        className="inline-block animate-spin rounded-full border-4 border-current border-r-transparent text-[#195790]"
        style={{ width: size, height: size, ...sxToStyle(sx), ...(style || {}) }}
    />
);

const Divider = ({ className = '', style, sx }) => (
    <hr className={`border-0 border-t border-slate-200 ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }} />
);

const IconButton = ({ children, className = '', color, edge, style, sx, type = 'button', ...props }) => (
    <button
        type={type}
        className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-[#195790] transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${className}`}
        style={{ ...sxToStyle(sx), ...(style || {}) }}
        {...props}
    >
        {children}
    </button>
);

const Paper = ({ children, className = '', elevation, style, sx }) => (
    <div className={`rounded-lg bg-white shadow ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
        {children}
    </div>
);

const ViewQuote = () => {
    const { primaryId } = useParams();
    const dispatch = useDispatch();
    const [quoteData, setQuoteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        if (primaryId) {
            dispatch(getQuoteById(primaryId))
                .then((data) => {
                    setQuoteData(data);
                    setLoading(false);
                })
                .catch(() => {
                    setQuoteData({ lineItems: [], totalAmount: 0, user: {} });
                    setSnackbar({
                        open: true,
                        message: 'Failed to load quote',
                        severity: 'error'
                    });
                    setLoading(false);
                });
        }
    }, [primaryId, dispatch]);

    const handleAcceptQuote = async () => {
        try {
            const updatedQuote = {
                ...quoteData,
                status: 'ACCEPTED'
            };

            await dispatch(updateQuoteWithType(updatedQuote, 'quote_status_update'));

            //   Show success snackbar
            setSnackbar({
                open: true,
                message: 'Quote accepted successfully',
                severity: 'success'
            });

            //   Auto-close after 3 seconds
            setTimeout(() => {
                setSnackbar(prev => ({ ...prev, open: false }));
            }, 3000);

        } catch {
            setSnackbar({
                open: true,
                message: 'Failed to accept quote',
                severity: 'error'
            });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </Box>
        );
    }

    const user = quoteData?.user || {};
    const lineItems = quoteData?.lineItems || [];

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    edge="start"
                    color="primary"
                    aria-label="go back"
                    onClick={() => window.history.back()}
                    sx={{ mr: 2 }}
                >
                    <ArrowBackIcon className="h-5 w-5" />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                    View Quote
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: '12px', padding: 4, background: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#f5f5f5', padding: 2, borderRadius: 2, mb: 3 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: 20, mr: 2 }}>
                        {user.firstName?.[0] || 'U'}
                    </Box>

                    <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon className="mr-2 h-5 w-5" />
                            {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                            <PhoneAndroidIcon className="mr-2 h-[18px] w-[18px]" />
                            {user.phoneNumber}
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    <strong>Total Amount:</strong> ₹{quoteData.totalAmount}
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                    Product List
                </Typography>
                <Grid container spacing={3}>
                    {lineItems.map((item, index) => (
                        <Grid item xs={12} key={index}>
                            <Card elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                {item.product?.imageContents?.length > 0 && (
                                    <Grid container spacing={1} sx={{ p: 2, pt: 2 }}>
                                        {item.product.imageContents.map((img, imgIndex) => (
                                            <Grid item xs={6} md={4} key={imgIndex}>
                                                <Box
                                                    component="img"
                                                    src={img.displayLink}
                                                    alt={`Product ${imgIndex + 1}`}
                                                    sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                <Divider />
                                <CardContent>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                        {item.product?.name || 'Unnamed Product'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <DescriptionIcon className="mr-2 h-[18px] w-[18px]" />
                                        {item.product?.description || 'No description'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <AttachMoneyIcon className="mr-2 h-[18px] w-[18px]" />
                                        ₹{item.product?.price?.priceAmt}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {quoteData.notes && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            <NotesIcon className="mr-2 inline h-5 w-5 align-middle" />
                            Notes
                        </Typography>
                        <Typography variant="body1" sx={{ ml: 4 }}>
                            {quoteData.notes}
                        </Typography>
                    </>
                )}

                <Box textAlign="center" mt={4}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAcceptQuote}
                        sx={{ minWidth: 180, fontWeight: 'bold' }}
                    >
                        Accept Quote
                    </Button>
                </Box>
            </Paper>

            <Snackbar
                autoHideDuration={4000}
                isOpen={snackbar.open}
                message={snackbar.message}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                tone={snackbar.severity}
            />
        </Container>
    );
};

export default ViewQuote;
